import { DialogContentText, Grid, TextField, DialogActions, Button } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ApolloError } from 'apollo-client';
import { Field, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import * as Yup from 'yup';
import { useForgotUsername } from '../../../hooks';
import { LanguageString, SuccessIconMessage } from '../../Common';

interface ForgotUsernameProps {
  toggle: () => void;
}

interface ForgotUsernameSchema {
  email: string;
}

interface FormStatus {
  submitted: boolean;
  message?: string;
}

export const ForgotUsername: React.FC<ForgotUsernameProps> = ({ toggle }) => {
  const formRef = useRef<FormikProps<ForgotUsernameSchema> | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [forgotUsername] = useForgotUsername();

  const initialValues: ForgotUsernameSchema = {
    email: ''
  };

  const initialStatus: FormStatus = {
    submitted: false,
    message: undefined
  };

  const Validation = Yup.object().shape({
    email: Yup.string().email('* Invalid Email').required('* Required')
  });

  const handleSubmit = (values: ForgotUsernameSchema, formikHelpers: FormikHelpers<ForgotUsernameSchema>) => {
    forgotUsername({
      variables: {
        input: {
          email: values.email
        }
      },
      update: (cache, result) => {
        if (result.data?.forgotUsername.success === true) {
          formikHelpers.setStatus({
            submitted: true,
            message: undefined
          });

          setSubmitted(true);
        } else {
          formikHelpers.setStatus({
            submitted: false,
            message: 'Oops. Something went wrong'
          });
        }
      }
    }).catch(res => {
      const errors: string[] = res.graphQLErrors.map((error: ApolloError) => {
        return error.message;
      });

      if (errors.length > 0) {
        formikHelpers.setStatus({
          submitted: false,
          message: errors[0]
        });
      }
    });
  };

  const localToggle = () => {
    setSubmitted(false);
    toggle();
  };

  return (
    <>
      <DialogContentText>
        <LanguageString
          groupName="GENERAL"
          resourceName="FORGOT_USERNAME_DESCRIPTION"
          defaultText="To view your username please enter your email address"
        />
      </DialogContentText>

      <Formik
        initialValues={initialValues}
        initialStatus={initialStatus}
        onSubmit={handleSubmit}
        enableReinitialize={true}
        validationSchema={Validation}
      >
        {formikProps => {
          formRef.current = formikProps;

          const status = formikProps.status as FormStatus;

          if (status.submitted) {
            return <SuccessIconMessage
              smallMessage
              animated
              message={
                <LanguageString
                  variant="subtitle1"
                  groupName="GENERAL"
                  resourceName="USERNAME_RESET_SUCCESS"
                  defaultText="If this user exists in the system they will receive an email with their username"
                />
              }
            />;
          }

          return (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Field validateOnBlur validateOnChange name="email">
                    {() => (
                      <TextField
                        fullWidth
                        id="forgot_username_email"
                        name="email"
                        label={<LanguageString groupName="GENERAL" resourceName="EMAIL" defaultText="Email" />}
                        onChange={formikProps.handleChange}
                        onBlur={formikProps.handleBlur}
                        error={Boolean(formikProps.errors.email && formikProps.touched.email)}
                        helperText={formikProps.errors.email && formikProps.touched.email && String(formikProps.errors.email)}
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>

              {(formikProps.status as FormStatus).message && (
                <Grid item xs={12}>
                  <Alert severity="error">{formikProps.status.message}</Alert>
                </Grid>
              )}
            </Form>
          );
        }}
      </Formik>
      <DialogActions>
        <Button onClick={localToggle} color="primary">
          <LanguageString groupName="GENERAL" resourceName="CANCEL" defaultText="Cancel" />
        </Button>
        {!submitted && (
          <Button variant="contained" onClick={() => formRef.current?.submitForm()} color="primary">
            <LanguageString groupName="GENERAL" resourceName="SUBMIT" defaultText="Submit" />
          </Button>
        )}
      </DialogActions>
    </>
  );
};
export default ForgotUsername;
